import { Check, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { ListingDetailsVehicleTest } from "@/types/listing";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";

interface TestItem {
  name: string;
  status: "ok" | "error";
  description?: string;
}

interface TestProtocolProps {
  isCollapsible?: boolean;
  vehicleTest: ListingDetailsVehicleTest;
  showComments?: boolean; // Control whether to show comments section
  commentsAsPopup?: boolean; // Control whether to show comments as popup
  guestMode?: boolean;
}

const TestProtocol = ({
  isCollapsible = true,
  vehicleTest,
  showComments = true, // Default to showing comments
  guestMode = false,
}: TestProtocolProps) => {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { baseText, subtleText } = useTextColorClass();
  const { language } = useLanguage();
  const tpLabels = carDetailsLabels(language).testProtocol;

  // Convert vehicleTest.vehicleTestData to testItems format
  const testItems: TestItem[] = [
    {
      name: tpLabels.testSpeed,
      status: vehicleTest.vehicleTestData?.speed ? "ok" : "error",
      description: vehicleTest.vehicleTestData?.speed
        ? `${vehicleTest.vehicleTestData.speed} ${tpLabels.kmh || "km/h"}`
        : tpLabels.notTested,
    },
    {
      name: tpLabels.vibrations,
      status:
        vehicleTest.vehicleTestData?.vibrations?.problem === "yes"
          ? "error"
          : "ok",
      description:
        vehicleTest.vehicleTestData?.vibrations?.problem === "yes"
          ? (vehicleTest.vehicleTestData.vibrations.location === "Annat"
              ? vehicleTest.vehicleTestData.vibrations.other
              : vehicleTest.vehicleTestData.vibrations.location) ||
            vehicleTest.vehicleTestData.vibrations.other ||
            tpLabels.problemDetected
          : tpLabels.noRemarks,
    },
    {
      name: tpLabels.leaks,
      status:
        vehicleTest.vehicleTestData?.leaks?.problem === "yes" ? "error" : "ok",
      description:
        vehicleTest.vehicleTestData?.leaks?.problem === "yes"
          ? (vehicleTest.vehicleTestData.leaks.source === "Annat" ||
            vehicleTest.vehicleTestData.leaks.source === tpLabels.other
              ? vehicleTest.vehicleTestData.leaks.other
              : vehicleTest.vehicleTestData.leaks.source) ||
            vehicleTest.vehicleTestData.leaks.other ||
            tpLabels.problemDetected
          : tpLabels.noRemarks,
    },
    {
      name: tpLabels.noise,
      status:
        vehicleTest.vehicleTestData?.noises?.problem === "yes" ? "error" : "ok",
      description:
        vehicleTest.vehicleTestData?.noises?.problem === "yes"
          ? (vehicleTest.vehicleTestData.noises.type === "Annat"
              ? vehicleTest.vehicleTestData.noises.other
              : vehicleTest.vehicleTestData.noises.type) ||
            vehicleTest.vehicleTestData.noises.other ||
            tpLabels.problemDetected
          : tpLabels.noRemarks,
    },
    {
      name: tpLabels.gearbox,
      status:
        vehicleTest.vehicleTestData?.gearbox?.problem === "yes"
          ? "error"
          : "ok",
      description:
        vehicleTest.vehicleTestData?.gearbox?.problem === "yes"
          ? (vehicleTest.vehicleTestData.gearbox.issue === "Annat" ||
            vehicleTest.vehicleTestData.gearbox.issue === tpLabels.other
              ? vehicleTest.vehicleTestData.gearbox.other
              : vehicleTest.vehicleTestData.gearbox.issue) ||
            vehicleTest.vehicleTestData.gearbox.other ||
            tpLabels.problemDetected
          : tpLabels.noRemarks,
    },
    // Krock section commented out as requested
    // {
    //   name: "Krock",
    //   status:
    //     vehicleTest.vehicleTestData?.crash?.problem === "yes" ||
    //     vehicleTest.vehicleTestData?.crash?.problem === "unknown"
    //       ? "error"
    //       : "ok",
    //   description:
    //     vehicleTest.vehicleTestData?.crash?.problem === "yes"
    //       ? (vehicleTest.vehicleTestData.crash.area === "Annat"
    //           ? vehicleTest.vehicleTestData.crash.other
    //           : vehicleTest.vehicleTestData.crash.area) ||
    //         vehicleTest.vehicleTestData.crash.other ||
    //         "Problem upptäckt"
    //       : vehicleTest.vehicleTestData?.crash?.problem === "unknown"
    //       ? "Vet ej"
    //       : "Inga anmärkningar",
    // },
    {
      name: tpLabels.ac,
      status:
        vehicleTest.vehicleTestData?.ac?.problem === "yes" ||
        vehicleTest.vehicleTestData?.ac?.problem === "not_tested"
          ? "error"
          : "ok",
      description:
        vehicleTest.vehicleTestData?.ac?.problem === "yes"
          ? (vehicleTest.vehicleTestData.ac.issue === "Annat" ||
            vehicleTest.vehicleTestData.ac.issue === tpLabels.other
              ? vehicleTest.vehicleTestData.ac.other
              : vehicleTest.vehicleTestData.ac.issue) ||
            vehicleTest.vehicleTestData.ac.other ||
            tpLabels.problemDetected
          : vehicleTest.vehicleTestData?.ac?.problem === "not_tested"
            ? tpLabels.notTested
            : tpLabels.noRemarks,
    },
    {
      name: tpLabels.dashboard,
      status:
        vehicleTest.vehicleTestData?.dashboard?.problem === "yes"
          ? "error"
          : "ok",
      description:
        vehicleTest.vehicleTestData?.dashboard?.problem === "yes"
          ? (vehicleTest.vehicleTestData.dashboard.issue === "Annat" ||
            vehicleTest.vehicleTestData.dashboard.issue === tpLabels.other
              ? vehicleTest.vehicleTestData.dashboard.other
              : vehicleTest.vehicleTestData.dashboard.issue) ||
            vehicleTest.vehicleTestData.dashboard.other ||
            tpLabels.problemDetected
          : tpLabels.noRemarks,
    },
    {
      name: tpLabels.windshield,
      status:
        vehicleTest.vehicleTestData?.windshield?.problem === "yes" ||
        vehicleTest.vehicleTestData?.windshield?.problem === "stone_chip"
          ? "error"
          : "ok",
      description:
        vehicleTest.vehicleTestData?.windshield?.problem === "yes"
          ? vehicleTest.vehicleTestData.windshield.description ||
            tpLabels.problemDetected
          : vehicleTest.vehicleTestData?.windshield?.problem === "stone_chip"
            ? tpLabels.stoneChip
            : tpLabels.noRemarks,
    },
  ];

  const displayTestItems: TestItem[] = guestMode
    ? testItems.map((item) => ({
        ...item,
        description: carDetailsLabels(language).guest.testProtocolHidden,
      }))
    : testItems;

  const renderTestItems = () => (
    <div id="test-protocol" className="space-y-1 sm:space-y-2">
      {displayTestItems.map((item, index) => (
        <div key={index} className={`p-2 md:p-2.5 rounded-lg`}>
          <div className="flex items-center justify-between mb-1">
            <span className={`${baseText} text-base sm:text-base`}>
              {item.name}
            </span>
            {item.status === "ok" ? (
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            ) : (
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            )}
          </div>
          {item.description && (
            <p
              className={`${subtleText} text-sm sm:text-sm ml-0 break-words`}
              style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
            >
              {item.description}
            </p>
          )}
        </div>
      ))}
      {/* Show comments inline if showComments is true */}
      {showComments && vehicleTest.comments && (
        <div
          className={`mt-4 p-2 sm:p-3 ${
            isDark ? "bg-black/20" : "bg-gray-100"
          } rounded-lg`}
        >
          <h3 className={`${baseText} text-sm sm:text-base mb-2`}>
            {carDetailsLabels(language).additionalComments.title}
          </h3>
          <p className={`${subtleText} text-sm sm:text-sm whitespace-pre-wrap`}>
            {vehicleTest.comments}
          </p>
        </div>
      )}
    </div>
  );

  if (!isCollapsible) {
    return renderTestItems();
  }

  return (
    <div
      className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}
    >
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="test-protocol">
          <AccordionTrigger
            className={`px-4 py-4 md:py-3 hover:bg-accent/20 flex justify-between`}
          >
            <span className={`font-semibold text-base md:text-base ${baseText}`}>
              {tpLabels.title}
            </span>
          </AccordionTrigger>
          <AccordionContent className={`p-4`}>
            {renderTestItems()}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default TestProtocol;
